import { useRef, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { Form, Formik } from "formik";
import { flowsValidation } from "./schemas/validations";
import { flow as flowSchema } from "./schemas/flow";
import FormField from "cxfcomponents/FormField";
import { truncateInputValue } from "helpers/fieldListeners";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useSlugFlow } from "hooks/useSlugFlow";
import { useIntegrations } from "hooks/useIntegrations";
import defaultFlowIcon from "assets/images/for-flow/flow-icon-list-view.svg";
import SoftBox from "components/SoftBox";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import CreatableSoftSelect from "components/CreatableSoftSelect";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import { createFlowIntegrationValues, uploadFile } from "helpers/common";
import { addIntegration } from "redux/actions/portal/integrationActionCreator";
import { updatePortalFlow } from "redux/actions/portal/flowsActionCreator";
import { updateFlow } from "api/FlowDesignerApis";
import { toast } from "react-toastify";
import { RecoverFlowDialog } from "layouts/Flows/components/RecoverFlowDialog";
import { syncFlowIntegrationDependencies } from "redux/actions/portal/resourceDependenciesActionCreator";

import { EditPictureMenu } from "components/Picture/Edit";
import { useFlowPermissions } from "hooks/useFlowPermissions";
import { DeleteFlowPermanently } from "layouts/Flows/components/DeleteFlowPermanently";

export const EditPropertiesDialog = ({ isOpen, onClose }) => {
   const { flow } = useSlugFlow();
   const formRef = useRef(null);
   const {
      formField: { flowName, description, status },
      formId
   } = flowSchema;
   const { flows } = usePortalFlows(false);
   const { integrations } = useIntegrations();
   const [selectedIntegrations, setSelectedIntegrations] = useState([]);
   const [pictureUpload, setPictureUpload] = useState(null);
   const [notifyOnTrash, setNotifyOnTrash] = useState(false);
   const [notifyOnDelete, setNotifyOnDelete] = useState(false);
   const [deleteFlow, setDeleteFlow] = useState(false);
   const { user } = useAuth0();
   const dispatch = useDispatch();
   const [showRecoverFlowDialog, setShowRecoverFlowDialog] = useState(false);
   const { canEdit, canDelete, canRetrieve } = useFlowPermissions(flow);

   const handleOpenRecoverDialog = async () => {
      try {
         if (flow.trashed) {
            setShowRecoverFlowDialog(true);
            return;
         }
      } catch (error) {
         console.log(error);
      }
   };

   const handleFormSubmit = async (values) => {
      const { id, updated_date_time, created_date_time, tenant_id, logoUpdatedAt, ...restFlow } = flow;

      const input = {
         ...restFlow,
         description: values.description || "",
         name: values.flowName,
         shared_status: values.shared_status ? "active" : "test",
         integrations: await createFlowIntegrationValues(
            (selectedIntegrations ?? []).map((integration) => integration.value),
            integrations,
            (newIntegration) => dispatch(addIntegration(newIntegration)),
            user,
            flow.id
         ),
         notify_for_moved_to_trash: notifyOnTrash,
         notify_for_delete: notifyOnDelete
      };

      let logoLastModified;

      if (pictureUpload) {
         const result = await uploadFile(user, pictureUpload, "flows", id, true);
         input.logo = result?.Location;
         logoLastModified = result?.LastModified;
      } else if (pictureUpload === "") {
         input.logo = "";
      }

      const updatedFlow = await updateFlow(user?.stripeId, flow.id, input, true);

      if (updatedFlow) {
         dispatch(
            syncFlowIntegrationDependencies({
               tenantId: user?.stripeId,
               flowId: flow.id,
               nextIntegrationIds: input.integrations
            })
         );
         input.id = flow.id;
         input.tenant_id = user?.stripeId;
         dispatch(
            updatePortalFlow({
               ...input,
               logoUpdatedAt: logoLastModified,
               id
            })
         );
         onClose();
      } else
         toast.error(`Unable to save modified flow - ensure that an instance of the cxf-executor is running and that the Flow Designer is connected to it. Then, reload and try again.`, {
            autoClose: 5000,
            pauseOnHover: true
         });
   };

   const validateFlowName = (value) => {
      let error;

      if (!value) {
         error = `${flowName.label} is required.`;
         return error;
      }

      if (flows && flows.length > 0) {
         const flowExists = flows.some((portalFlow) => portalFlow.id !== flow?.id && portalFlow.name.toLowerCase() === value.toLowerCase());
         if (flowExists) {
            error = `${value} already exists.`;
            return error;
         }
      }
   };

   const createIntegrationsOptions = () => {
      const mappedIntegrations =
         integrations?.map((integration) => ({
            value: integration.id,
            label: integration.integration_name
         })) ?? [];

      return mappedIntegrations;
   };

   const getInitialValues = () => {
      return {
         [flowName.name]: flow?.name,
         [description.name]: flow?.description,
         [status.name]: flow?.shared_status === "active"
      };
   };

   const handleRecoverFlow = async () => {
      const { created_date_time, id, tenant_id, userAccessType, updated_date_time, ...restFlow } = flow;
      const input = {
         ...restFlow,
         trashed: false
      };

      const updatedFlow = await updateFlow(user?.stripeId, flow.id, input, false);

      if (updatedFlow) {
         toast.success("Flow has been recovered");

         input.id = flow.id;
         input.tenant_id = user?.stripeId;

         dispatch(updatePortalFlow(input));
         onClose();
         setShowRecoverFlowDialog(false);
      } else
         toast.error(`Unable to save recovered flow - ensure that an instance of the cxf-executor is running and that the Flow Designer is connected to it. Then, reload and try again.`, {
            autoClose: 5000,
            pauseOnHover: true
         });
   };

   useEffect(() => {
      if (!flow?.integrations) return;

      const flowIntegrations = JSON.parse(flow.integrations);

      const filteredIntegrations = integrations
         ?.filter((integration) => flowIntegrations.includes(integration.id))
         ?.map((integration) => ({
            value: integration.id,
            label: integration.integration_name
         }));

      if (!isOpen) {
         setSelectedIntegrations(filteredIntegrations);
         setPictureUpload(null);
         setNotifyOnDelete(false);
         setNotifyOnTrash(false);
         setDeleteFlow(false);

         return;
      }

      setSelectedIntegrations(filteredIntegrations);
   }, [integrations, flow, isOpen]);

   useEffect(() => {
      if (isOpen && flow) {
         setNotifyOnDelete(flow.notify_for_delete);
         setNotifyOnTrash(flow.notify_for_moved_to_trash);
      }
   }, [isOpen, flow]);

   if (!flow) return <></>;

   return (
      <>
         <Dialog open={isOpen} onClose={onClose} fullWidth={true} maxWidth="csm">
            <DialogTitle
               sx={{
                  marginBottom: "20px",
                  padding: "32px 0 0 48px"
               }}
               className="white-to-dark"
               data-testid="form-title"
            >
               Edit Properties
            </DialogTitle>
            <DialogContent
               sx={{
                  padding: "0 48px"
               }}
            >
               <Formik initialValues={getInitialValues()} validationSchema={flowsValidation} onSubmit={handleFormSubmit} innerRef={formRef}>
                  {({ errors, setFieldValue, values }) => {
                     return (
                        <Form id={formId} autoComplete="off">
                           <SoftBox
                              className="border-top"
                              sx={{
                                 alignItems: "flex-start",
                                 display: "flex",
                                 paddingTop: "20px"
                              }}
                           >
                              <EditPictureMenu
                                 pictureUpload={pictureUpload}
                                 setPictureUpload={setPictureUpload}
                                 value={flow?.logo}
                                 params={flow.logoUpdatedAt ?? flow.updated_date_time}
                                 isReadOnlyAccess={!canEdit}
                                 defaultPicture={defaultFlowIcon}
                              />
                              <SoftBox
                                 sx={{
                                    flexGrow: 1,
                                    transform: "translateY(-12px)"
                                 }}
                              >
                                 <FormField
                                    type={flowName.type}
                                    label={flowName.label}
                                    name={flowName.name}
                                    validate={validateFlowName}
                                    onInput={truncateInputValue(50)}
                                    error={errors[flowName]}
                                    placeholder={flowName.placeholder ? flowName.placeholder : ""}
                                    required={true}
                                    disabled={!canEdit}
                                 />
                                 <FormField
                                    type={description.type}
                                    label={description.label}
                                    name={description.name}
                                    onInput={truncateInputValue(500)}
                                    error={errors[description]}
                                    placeholder={description.placeholder ? description.placeholder : ""}
                                    isMultiline
                                    rows={2}
                                    disabled={!canEdit}
                                 />
                              </SoftBox>
                           </SoftBox>
                           <SoftBox
                              className="border-top"
                              sx={{
                                 paddingTop: "20px"
                              }}
                           >
                              <SoftBox
                                 sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    marginBottom: "14px"
                                 }}
                              >
                                 <SoftTypography
                                    className="white-to-dark"
                                    sx={{
                                       fontSize: "14px",
                                       marginRight: "24px",
                                       fontWeight: 700
                                    }}
                                 >
                                    Active
                                 </SoftTypography>
                                 <Switch
                                    className="flow-active-switch custom"
                                    checked={values.shared_status}
                                    onChange={(event) => {
                                       setFieldValue(status.name, event.target.checked);
                                    }}
                                    disabled={flow.trashed || !canEdit}
                                 />
                              </SoftBox>
                              <SoftBox sx={{ marginBottom: "20px" }}>
                                 <SoftTypography variant="h6" mb={1} sx={{ fontSize: "12px" }}>
                                    Integrations
                                 </SoftTypography>
                                 <CreatableSoftSelect
                                    className="responsive-multi-height"
                                    placeholder="Select Integrations"
                                    options={createIntegrationsOptions()}
                                    isMulti={true}
                                    value={selectedIntegrations}
                                    sx={{ zIndex: 0 }}
                                    hideSelectedOptions={false}
                                    onChange={(selectedOptions, meta) => {
                                       if (meta.action === "create-option") {
                                          const createdOption = meta.option;

                                          // this means there are already created options with the new value
                                          if (selectedIntegrations.some((option) => option.value.toLowerCase().trim() === createdOption.value.toLowerCase().trim())) {
                                             return;
                                          }
                                       }

                                       setSelectedIntegrations(selectedOptions ?? []);
                                    }}
                                    isDisabled={!canEdit}
                                 />
                              </SoftBox>
                              <SoftBox
                                 className="border-top"
                                 sx={{
                                    paddingTop: "20px"
                                 }}
                              >
                                 {flow.trashed && (
                                    <>
                                       <SoftTypography
                                          sx={{
                                             fontSize: "13px",
                                             fontWeight: 700,
                                             marginBottom: "4px"
                                          }}
                                          className="white-to-dark"
                                       >
                                          Recover Flow
                                       </SoftTypography>
                                       <SoftTypography
                                          sx={{
                                             fontSize: "14px",
                                             marginBottom: "8px"
                                          }}
                                       >
                                          Recover this flow in order to edit the actual content inside the designer
                                       </SoftTypography>
                                       <SoftButton
                                          sx={{
                                             marginBottom: "14px"
                                          }}
                                          className="button-trash"
                                          variant="outlined"
                                          color="secondary"
                                          onClick={handleOpenRecoverDialog}
                                          disabled={!canRetrieve}
                                       >
                                          recover flow
                                       </SoftButton>
                                    </>
                                 )}

                                 {canDelete && (
                                    <>
                                       <SoftTypography
                                          sx={{
                                             fontSize: "14px",
                                             marginBottom: "8px"
                                          }}
                                       >
                                          Deleting forever will permanently delete this flow and it cannot be retrieved. Check the box to confirm deleting forever.
                                       </SoftTypography>
                                       <SoftBox
                                          sx={{
                                             alignItems: "flex-start",
                                             display: "flex",
                                             marginBottom: "14px"
                                          }}
                                       >
                                          <Checkbox
                                             id="confirmDelete"
                                             style={{
                                                marginRight: "20px"
                                             }}
                                             checked={deleteFlow}
                                             onChange={(event) => {
                                                setDeleteFlow(event.target.checked);
                                             }}
                                             disabled={!canEdit}
                                          />
                                          <SoftTypography
                                             component="label"
                                             htmlFor="confirmDelete"
                                             sx={{
                                                fontSize: "12px"
                                             }}
                                          >
                                             I’m sure I want to delete this flow.
                                          </SoftTypography>
                                       </SoftBox>
                                       <DeleteFlowPermanently item={flow} deleteAvailable={deleteFlow} />
                                    </>
                                 )}
                              </SoftBox>
                              <SoftBox
                                 className="border-top"
                                 sx={{
                                    paddingTop: "20px",
                                    marginTop: "20px"
                                 }}
                              />
                           </SoftBox>
                        </Form>
                     );
                  }}
               </Formik>
            </DialogContent>
            <DialogActions
               sx={{
                  padding: "0 48px 32px"
               }}
            >
               <SoftButton variant="outlined" color="info" onClick={onClose}>
                  cancel
               </SoftButton>
               <SoftButton disabled={!canEdit} color="primary" onClick={() => formRef.current.submitForm()}>
                  save
               </SoftButton>
            </DialogActions>
         </Dialog>
         <RecoverFlowDialog open={showRecoverFlowDialog} onAccept={handleRecoverFlow} onClose={() => setShowRecoverFlowDialog(false)} flow={flow} />
      </>
   );
};
